import {Stack, Typography, useTheme} from '@mui/material';
import React, {Fragment} from 'react';
import {useShallow} from 'zustand/react/shallow';

import {FiveStarsEnum} from 'main/analytics/gaTagsEnum';
import {translations} from 'presentation/translations-new';

import {FiveStarsRatings} from '../FiveStarsRatings';
import {Motivators} from '../Motivators';
import {OtherComments} from '../OtherComments';
import {StyledCard, StyledCardContent, StyledDescription, StyledTitle} from './styles';
import {RatingFormProps} from './types';
import {useAdditionalQuestionsErrorStore} from '../../stores/useAdditionalQuestionsErrorStore';
import {useMotivatorsStore} from '../../stores/useMotivatorsStore';
import {useRatingErrorStore} from '../../stores/useRatingErrorStore';
import {useRatingValuesStore} from '../../stores/useRatingValuesStore';

const {rateToAdvance} = translations['pt-br'].privateFlow.fiveStarsRating;

export function RatingForm({motivator}: RatingFormProps) {
	const {palette} = useTheme();

	const motivatorId = motivator.id;

	const ratingValue = useRatingValuesStore(state => state.ratingValues[motivatorId] ?? null);
	const setRatingValue = useRatingValuesStore(state => state.setRatingValue);

	const ratingError = useRatingErrorStore(state => state.ratingError);
	const setRatingError = useRatingErrorStore(state => state.setRatingError);
	const additionalQuestionsError = useAdditionalQuestionsErrorStore(
		state => state.additionalQuestionsError,
	);

	const motivatorsValues = useMotivatorsStore(
		useShallow(state => state.motivatorsValues[motivatorId] || []),
	);
	const setMotivatorsValues = useMotivatorsStore(state => state.setMotivatorValue);
	const resetMotivatorsValues = useMotivatorsStore(state => state.resetMotivatorValues);
	const resetAdditionalQuestionsError = useAdditionalQuestionsErrorStore(
		state => state.resetAdditionalQuestionsError,
	);

	const handleChangeMotivators = (
		e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
		index: number,
	) => {
		setMotivatorsValues(motivator, index, e.target.value);
	};

	const showMotivators = ratingValue !== null && ratingValue <= 3;

	return (
		<Stack gap={2}>
			<StyledCard>
				<StyledCardContent>
					<Stack gap={0.5}>
						<StyledTitle data-tag-id={FiveStarsEnum.VISUALIZOU_PERGUNTA}>
							Como você avalia o(a) <b>{motivator.titulo}</b>?
						</StyledTitle>
						<StyledDescription>{motivator.descricao}</StyledDescription>
					</Stack>
					<FiveStarsRatings
						value={ratingValue}
						data-tag-id={FiveStarsEnum.CLICK_ESTRELAS_AVALIACAO}
						onChange={(_, newValue) => {
							setRatingError(false);
							setRatingValue(motivatorId, newValue);
							if (newValue && newValue > 3) {
								resetMotivatorsValues(motivator);
								resetAdditionalQuestionsError();
							}
						}}
						hasError={ratingError}
					/>
				</StyledCardContent>
				{ratingError ? (
					<Typography marginLeft={1} variant="helperText" color={palette.error.main}>
						{rateToAdvance}
					</Typography>
				) : null}
			</StyledCard>
			{showMotivators ? (
				<Stack data-tag-id={FiveStarsEnum.CONTAINER_PERGUNTAS} gap={2}>
					{motivator.questoes_3_estrelas_abaixo.map((mot, index) => (
						<Fragment key={mot.id_quest_3estrelas_abaixo}>
							{mot.descricao === 'Outros' ? (
								<OtherComments
									name={mot.descricao}
									value={motivatorsValues[index].resposta}
									onChange={e => handleChangeMotivators(e, index)}
								/>
							) : (
								<Motivators
									motivator={mot}
									value={motivatorsValues[index].resposta}
									name={mot.descricao}
									onChange={e => handleChangeMotivators(e, index)}
									hasError={
										additionalQuestionsError &&
										!motivatorsValues[index].resposta
									}
								/>
							)}
						</Fragment>
					))}
				</Stack>
			) : null}
		</Stack>
	);
}
